import {
  Button,
  Popover,
  PopoverSurface,
  PopoverTrigger,
} from "@fluentui/react-components";
import { useBoolean } from "@fluentui/react-hooks";
import { InfoRegular } from "@fluentui/react-icons";
import { useContext } from "react";
import { LayoutContext } from "../../../../../context/LayoutContext";
import { $sky60 } from "../../../../../cssjs/colors";
import { FlightCardTypes } from "../../../../../enum/flights/FlightEnum";
import { GlobalFilterOptionsEnum } from "../../../../../enum/global-filter/GlobalFilterEnum";
import { formatRelativeTime } from "../../../../../utils/common/DateHelperFunctions";
import { flightTableViewTypes } from "../../../../../utils/flights/FlightConstants";
import { FlightSummaryHelper } from "../../../../../utils/flights/FlightSummaryHelper";
import { StatusIconSelector } from "../../../../common/icon-selectors/StatusIconSelector";
import { KpiToolTipInfo } from "../../../../flights/flight-kpi/KpiToolTipInfo";

type KPIHeaderProps = {
  cardName: FlightCardTypes;
  data: any;
  showInformation?: boolean;
  selectedFilter: any;
  kpiConfigData: any;
  hopSettingsData: any;
  kpiType: any;
};

export const KPIHeader: React.FunctionComponent<KPIHeaderProps> = ({
  cardName,
  data,
  showInformation = true,
  selectedFilter,
  kpiConfigData,
  hopSettingsData,
  kpiType,
}): React.JSX.Element => {
  const [isInfoVisible, { toggle: toggleIsInfoCalloutVisible }] =
    useBoolean(false);
  const isRolling = selectedFilter === GlobalFilterOptionsEnum.rolling;
  const isToday = selectedFilter === GlobalFilterOptionsEnum.today;

  const { currentTime } = useContext(LayoutContext);
  const startDateText = formatRelativeTime(
    kpiConfigData?.filterDate.startDate,
    currentTime
  );
  const endDateText = formatRelativeTime(
    kpiConfigData?.filterDate.endDate,
    currentTime
  );

  const kpiCalulcationTextContent =
    isRolling || isToday ? (
      <>
        This KPI is calculated from <i>{startDateText}</i> to{" "}
        <i>{endDateText}</i>.
      </>
    ) : (
      ""
    );

  const { label } = flightTableViewTypes[cardName] || { label: "" };

  const Buffers = FlightSummaryHelper.getBufferValue(hopSettingsData, kpiType);

  const overall = Array.isArray(data)
    ? data.find((item: any) => item.type === "Overall")
    : 0;

  return (
    <div data-testid="kpi-header">
      <header
        className={`d-flex amd-container-hspacebtwn`}
        style={{ alignItems: "flex-start" }}
      >
        <div className="d-flex amd-container-vcolumn">
          <div className="d-flex" style={{ alignItems: "center" }}>
            <div className="fs-40 pr-sm-1">
              {`${Math.round(overall?.kpiValue || 0)}%`}
            </div>
            <div
              className={`d-flex amd-container-vcolumn`}
              style={{
                flexDirection: "column",
                minWidth: 0,
              }}
            >
              <StatusIconSelector status={overall?.status} />
              <div
                className="fs-14 fw-500"
                style={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                  flex: "1 1 auto",
                }}
              >
                {label}
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex" style={{ cursor: "pointer" }}>
          {showInformation && (
            <Popover
              open={isInfoVisible}
              onOpenChange={toggleIsInfoCalloutVisible}
              positioning="below-end"
            >
              <PopoverTrigger>
                <Button
                  style={{
                    border: "none",
                    background: "transparent",
                  }}
                  data-testid="info-button"
                  onClick={(event: any) => {
                    event.stopPropagation();
                    event.preventDefault();
                  }}
                  icon={
                    <InfoRegular
                      style={{
                        fontSize: "18px",
                        color: $sky60,
                      }}
                    />
                  }
                  iconPosition="after"
                />
              </PopoverTrigger>
              <PopoverSurface>
                <div style={{ maxWidth: "250px" }}>
                  <KpiToolTipInfo Buffers={Buffers} bufferName={cardName} />
                  <br />
                  {kpiCalulcationTextContent}
                </div>
              </PopoverSurface>
            </Popover>
          )}
        </div>
      </header>
    </div>
  );
};
